<script setup>
import { useI18n } from 'vue-i18n'
import { VFileUpload, VFileUploadItem } from 'vuetify/labs/VFileUpload'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useCustomerStore } from '@/stores/CustomersStore'
import { useModalStore } from '@/stores/ModalStore'
import { abortBackendRequest } from '@/utils/backend'
import { useDisplay } from "vuetify"
import { slugify } from "@/utils/strings"
import MediaPreviewDialog from "@/components/dialogs/MediaPreviewDialog.vue"
import { saveBlob } from "@/utils/saveBlob"
import { showErrorToast } from "@/utils/toast.js"

const props = defineProps({
  density: {
    type: String,
    default: 'default',
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  maxFileSize: {
    type: Number,
    default: 5 * 1024 * 1024, // 5MB default max file size
    description: 'Maximum allowed file size in bytes',
  },
})

const emit = defineEmits(['update:modelValue', 'fileUploaded', 'fileDeleted', 'fileDownloaded', 'fileError'])

const { t } = useI18n()
const { mobile } = useDisplay()

// Get customer store for API operations
const customerStore = useCustomerStore()
const modalStore = useModalStore()

// Keep internal files for new uploads only
const userFiles = ref([]) // Used as v-model in directly in FileUploader, visible when uploading only
const files = ref([]) // The list under the file uploader

const isUploading = ref(false)
const downloadingFiles = ref(new Set())
const deletingFiles = ref(new Set())

const fileError = ref('') // Store for file errors

// Latest files on top
const sortedFiles = computed(() => {
  return [...files.value].reverse()
})

// Watch for external changes to files
watch(() => props.modelValue, (newFiles) => {
  files.value = newFiles
}, { deep: true })


// Fetch customer files
const fetchFiles = async () => {
  if (!customerStore.selectedCustomer.salonerClientId) return

  try {
    if (customerStore.gatheredUserInfo?.attachments) {
      files.value = customerStore.gatheredUserInfo.attachments.map(file => ({
        id: file.id,
        name: file.originalName,
        status: 'uploaded',
      }))


      emit('update:modelValue', files.value)
    }
  } catch (error) {
    console.error('Error fetching files:', error)
  }
}

// 👉 Call file upload
const handleFileUpload = async (uploadedFiles) => {
  if (!uploadedFiles || !uploadedFiles.length) return

  fileError.value = '' // Clear previous errors
  isUploading.value = true

  try {
    for (const file of uploadedFiles) {
      if (file instanceof File) {
        // Check file size
        if (file.size > props.maxFileSize) {
          fileError.value = t('fileTooLarge', {
            name: file.name,
            max: props.maxFileSize / (1024 * 1024),
          })

          showErrorToast(fileError.value)
          emit('fileError', fileError.value)

          continue
        }

        // Create hash for the file
        const hashString = `${file.name}-${file.size}-${file.lastModified}`
        const safe = slugify(hashString)
        const hash = btoa(safe)

        // Upload file using customer store
        if (customerStore.selectedCustomer.salonerClientId) {
          const success = await customerStore.uploadFile(customerStore.selectedCustomer.salonerClientId, file, hash)

          if (success) {
            // Refresh files after upload
            await fetchFiles()
            showToast(t('fileUploadSuccess'), 'success')
          }
        }
      }
    }
  } catch (error) {
    console.error('File upload error:', error)
  } finally {
    // Clear upload component after processing
    userFiles.value = []
    isUploading.value = false
  }
}

//  👉 Call file deletation
const removeFile = async (fileId) => {
  const index = files.value.findIndex(file => file.id === fileId)
  if (index < 0) return

  const file = files.value[index]

  deletingFiles.value.add(fileId)

  try {

    if (file.id && customerStore.selectedCustomer.salonerClientId) {

      const confirmed = await modalStore.openConfirmationModal(t('confirmDelete'), t('warning'))

      if (confirmed) {
        const response = await customerStore.deleteFile(customerStore.selectedCustomer.salonerClientId, file.id)

        if (response) {
          // Update local file list after successful API deletion
          const newFiles = [...files.value]
          newFiles.splice(index, 1)

          files.value = newFiles
          showToast(t('file_deleted_successfully'), 'success')

        }
      }


    } else {
      // For files without IDs or when there's no customer ID
      const newFiles = [...files.value]
      newFiles.splice(index, 1)
      files.value = newFiles


    }
  } catch (error) {
    // console.error('Error deleting file:', error)
  } finally {
    deletingFiles.value.delete(fileId)
  }
}

// 👉 Attachments handling: downloading and previewing
const previewedFileIndex = ref(0)


// 👉 Call file download
const handleFileDownload = async (fileId) => {
  const index = files.value.findIndex(file => file.id === fileId)
  if (index < 0) return

  const file = files.value[index]
  downloadingFiles.value.add(fileId) // Store file ID

  try {
    if (file.id) {
      const blob = await customerStore.downloadFile(file.id)
      if (!blob) return

      await saveBlob(
        blob,
        file.originalName || file.name || `file-${fileId}`,
      )
    }
  } catch (error) {
    // console.error('Error downloading file:', error)
  } finally {
    downloadingFiles.value.delete(fileId)
  }
}

// 👉 Previewing files
const previewFileDialog = ref(false) // Dialog visibility
const previewError = ref('')
const previewSourcesCache = ref(new Map()) // Cache for preview sources by file ID

// Files prepared for MediaPreviewDialog
const previewFiles = computed(() => {
  return sortedFiles.value.map(file => ({
    src: previewSourcesCache.value.get(file.id) || '',
    name: file.name || file.originalName || '',
    type: (file.name || file.originalName || '').toLowerCase().endsWith('.pdf') ? 'pdf' : 'image',
  }))
})

const handleFilePreview = async (fileId) => {
  const file = sortedFiles.value.find(f => f.id === fileId)
  if (!file) return

  previewFileDialog.value = true
  previewedFileIndex.value = sortedFiles.value.findIndex(f => f.id === fileId)
  previewError.value = ''

  if (file.id) {
    // Check if we already have the preview source cached
    if (!previewSourcesCache.value.has(fileId)) {
      const result = await customerStore.generateFilePreviewLink(fileId)

      if (result) {
        previewSourcesCache.value.set(fileId, result)
        // Trigger reactivity
        previewSourcesCache.value = new Map(previewSourcesCache.value)
      } else {
        previewError.value = t('file_error')
      }
    }
  }
}

// Handle navigation in preview dialog - load new file source
const handlePreviewChange = async (newIndex) => {
  const file = sortedFiles.value[newIndex]
  if (!file) return

  previewedFileIndex.value = newIndex
  previewError.value = ''

  if (file.id) {
    // Check if we already have the preview source cached
    if (!previewSourcesCache.value.has(file.id)) {
      const result = await customerStore.generateFilePreviewLink(file.id)

      if (result) {
        previewSourcesCache.value.set(file.id, result)
        // Trigger reactivity
        previewSourcesCache.value = new Map(previewSourcesCache.value)
      } else {
        previewError.value = t('file_error')
      }
    }
  }
}

// 👉 Abort the upload process
const abortUpload = (file) => {
  const hashString = `${file.name}-${file.size}-${file.lastModified}`
  const safe = slugify(hashString)
  const hash = btoa(safe) // Convert to base64
  // Abort the upload request to the backend
  abortBackendRequest(`/files/upload?hash=${hash}`)

  isUploading.value = false

  showToast(t('aborted'), 'info')
}

// 👉 Method to clear all files
const clearFiles = () => {
  files.value = []
}

defineExpose({
  removeFile,
  clearFiles,
  fetchFiles,
})

// Fetch files from customer when component mounts
onMounted(async () => {
  if (customerStore.selectedCustomer.salonerClientId) {
    await fetchFiles()
  }
})

onBeforeUnmount(() => {
  // Clean up any cached preview URLs
  previewSourcesCache.value.forEach((url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  })
  previewSourcesCache.value.clear()
})
</script>

<template>
  <VFileUpload
    v-model="userFiles"
    class="sal-file-uploader"
    :divider-text="t('or')"
    :browse-text="t('search')"
    :density="props.density"
    multiple
    clearable
    show-size
    @update:model-value="handleFileUpload"
  >
    <template #icon>
      <VIcon
        :size="mobile ? '26' : 'default'"
        icon="ri-file-line"
        class="ml-5"
      />
    </template>
    <template #item="{ props: itemProps, file }">
      <VFileUploadItem
        v-bind="itemProps"
        lines="one"
        nav
      >
        <template #prepend>
          <VProgressCircular
            class="mr-4"
            indeterminate
          />
        </template>
        <span>{{ $t('uploading') }} ...</span>
        <template #clear="{ props: clearProps }">
          <VBtn
            color="primary"
            v-bind="clearProps"
            @click="abortUpload(file)"
          />
        </template>
      </VFileUploadItem>
    </template>
    <template #title>
      <div class="d-flex flex-column ga-2 pa-2">
        <p class="text-h6 text-md-h4 mb-0">
          {{ t('dragDropFiles') }}
        </p>
        <p class="text-caption text-high-emphasis  mb-0">
          {{ $t('maxFileSize') }}:
          <VChip size="x-small">
            5MB
          </VChip>
        </p>
      </div>
    </template>
  </VFileUpload>

<!--    max-height="350px"-->
  <VList
    v-if="files.length > 0"
    density="compact"
  >
    <VListItem
      v-for="(file, index) in sortedFiles"
      :key="index"
      :disabled="isUploading"
      class="mb-2 border-thin border-rounded-lg px-2"
    >
      <div class="d-flex align-center lh-1">
        <VIcon
          icon="ri-file-line"
          class="mr-2"
        />
        <span
          class="text-no-truncate"
          style="max-width: calc(100% - 30px)"
        >{{ file.name || file.originalName }}</span>
      </div>

      <template #append>
        <div class="d-flex ga-2">
          <VBtn
            icon
            variant="text"
            color="error"
            size="small"
            :loading="deletingFiles.has(file.id)"
            :disabled="deletingFiles.has(file.id) || downloadingFiles.has(file.id)"
            @click="removeFile(file.id)"
          >
            <VIcon icon="ri-delete-bin-line" />
            <VTooltip
              v-if="!mobile"
              activator="parent"
            >
              {{ $t('delete') }}
            </VTooltip>
          </VBtn>

          <!-- v-if is jpg, png, pdf -->
          <VBtn
            v-if="/\.(jpe?g|png|pdf)$/i.test(file.name)"
            icon
            variant="text"
            color="gray"
            size="small"
            :disabled="deletingFiles.has(file.id) || downloadingFiles.has(file.id)"
            @click="handleFilePreview(file.id)"
          >
            <VIcon icon="ri-eye-line" />
            <VTooltip
              v-if="!mobile"
              activator="parent"
            >
              {{ $t('preview') }}
            </VTooltip>
          </VBtn>

          <VBtn
            icon
            variant="text"
            color="gray"
            size="small"
            :loading="downloadingFiles.has(file.id)"
            :disabled="downloadingFiles.has(file.id) || deletingFiles.has(file.id)"
            @click="handleFileDownload(file.id)"
          >
            <VIcon icon="ri-download-line" />
            <VTooltip
              v-if="!mobile"
              activator="parent"
            >
              {{ $t('download') }}
            </VTooltip>
          </VBtn>
        </div>
      </template>
    </VListItem>
  </VList>

  <p
    v-else
    type="info"
    class="mt-8 text-center"
  >
    {{ $t('noFiles') }}
  </p>
  <!-- Media Preview Dialog -->
  <MediaPreviewDialog
    v-model="previewFileDialog"
    :files="previewFiles"
    :initial-index="previewedFileIndex"
    :loading="customerStore.isLoading"
    :error="previewError"
    @change="handlePreviewChange"
  />
</template>

<style lang="scss">
.v-file-upload.v-sheet {
  padding-block: 1.5rem;
}

.sal-file-uploader {
  .v-file-upload-icon {
    display: none !important;
  }
}
</style>


